import { Box, Stack, Text } from "@repo/ui/core";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useSlideContext } from "@contexts/SlideContext";
import { SlideTitle } from "@components/SlideTitle";
import {
  DOMAINS,
  SUBGROUPS,
  NODES,
  ARROWS,
  CH,
  NW,
  NH,
  domainMap,
  nodeMap,
  absPos,
  edgePt,
  type NodeDef,
  type NodeColorTheme,
} from "@components/architecture-data";
import classes from "./DiagramSlide.module.css";

const EXTENDED_CW = 1650;

const DOMAIN_COLORS: Record<
  string,
  { fill: string; stroke: string; label: string }
> = {
  domainBlue: {
    fill: "rgba(37,99,235,0.06)",
    stroke: "rgba(96,165,250,0.25)",
    label: "#93c5fd",
  },
  domainAmber: {
    fill: "rgba(234,138,17,0.06)",
    stroke: "rgba(234,138,17,0.25)",
    label: "#fdba74",
  },
  domainTeal: {
    fill: "rgba(34,211,238,0.05)",
    stroke: "rgba(34,211,238,0.25)",
    label: "#67e8f9",
  },
  domainViolet: {
    fill: "rgba(139,92,246,0.06)",
    stroke: "rgba(139,92,246,0.25)",
    label: "#c4b5fd",
  },
};

const NODE_COLORS: Record<
  NodeColorTheme,
  { fill: string; stroke: string; iconBg: string; label: string }
> = {
  blue: {
    fill: "rgba(37,99,235,0.10)",
    stroke: "rgba(96,165,250,0.25)",
    iconBg: "#2563eb",
    label: "#93c5fd",
  },
  amber: {
    fill: "rgba(234,138,17,0.10)",
    stroke: "rgba(234,138,17,0.25)",
    iconBg: "#ea8a11",
    label: "#fdba74",
  },
  teal: {
    fill: "rgba(34,211,238,0.08)",
    stroke: "rgba(34,211,238,0.25)",
    iconBg: "#0891b2",
    label: "#67e8f9",
  },
  violet: {
    fill: "rgba(139,92,246,0.10)",
    stroke: "rgba(139,92,246,0.25)",
    iconBg: "#7c3aed",
    label: "#c4b5fd",
  },
  gray: {
    fill: "rgba(255,255,255,0.025)",
    stroke: "rgba(255,255,255,0.08)",
    iconBg: "#4b5563",
    label: "#d1d5db",
  },
  green: {
    fill: "rgba(74,222,128,0.08)",
    stroke: "rgba(74,222,128,0.25)",
    iconBg: "#15803d",
    label: "#86efac",
  },
};

export type StepArrow = {
  from: string;
  to: string;
  color: string;
  bidirectional?: boolean;
};

export type StepDef = {
  key: string;
  label: string;
  nodes: Set<string>;
  arrows: StepArrow[];
  caption: string;
};

export type DiagramSlideProps = {
  title: string;
  subtitle?: string;
  steps: StepDef[];
  extraNodes?: NodeDef[];
};

function resolveAbsPos(n: NodeDef): { x: number; y: number } {
  if (!n.subgroupId && !n.domainId) return { x: n.x, y: n.y };
  return absPos(n);
}

function resolveCentre(
  n: NodeDef,
): { x: number; y: number } {
  const p = resolveAbsPos(n);
  return { x: p.x + NW / 2, y: p.y + NH / 2 };
}

function computePath(
  fromId: string,
  toId: string,
  extraNodeMap: Map<string, NodeDef>,
): string | null {
  const fn = extraNodeMap.get(fromId) ?? nodeMap.get(fromId);
  const tn = extraNodeMap.get(toId) ?? nodeMap.get(toId);
  if (!fn || !tn) return null;

  const fc = resolveCentre(fn);
  const tc = resolveCentre(tn);
  const p1 = edgePt(fc.x, fc.y, tc.x, tc.y);
  const p2 = edgePt(tc.x, tc.y, fc.x, fc.y);

  const mx = (p1.x + p2.x) / 2;
  const my = (p1.y + p2.y) / 2;
  const len = Math.hypot(p2.x - p1.x, p2.y - p1.y);
  const bend = Math.min(55, len * 0.15);
  const nx = -(p2.y - p1.y) / (len || 1);
  const ny = (p2.x - p1.x) / (len || 1);
  const cx = mx + nx * bend;
  const cy = my + ny * bend;

  return `M${p1.x},${p1.y} Q${cx},${cy} ${p2.x},${p2.y}`;
}

export function DiagramSlide({
  title,
  subtitle,
  steps,
  extraNodes = [],
}: DiagramSlideProps) {
  const [step, setStep] = useState(0);
  const {
    registerNextInterceptor,
    unregisterNextInterceptor,
    registerPrevInterceptor,
    unregisterPrevInterceptor,
  } = useSlideContext();

  const totalSteps = steps.length;

  useEffect(() => {
    if (step < totalSteps - 1) {
      registerNextInterceptor(() => setStep((s) => s + 1));
    } else {
      unregisterNextInterceptor();
    }
    return () => unregisterNextInterceptor();
  }, [step, totalSteps, registerNextInterceptor, unregisterNextInterceptor]);

  useEffect(() => {
    if (step > 0) {
      registerPrevInterceptor(() => setStep((s) => s - 1));
    } else {
      unregisterPrevInterceptor();
    }
    return () => unregisterPrevInterceptor();
  }, [step, registerPrevInterceptor, unregisterPrevInterceptor]);

  const currentStep = steps[step]!;
  const extraNodeMap = new Map(extraNodes.map((n) => [n.id, n]));
  const extraNodeIds = new Set(extraNodes.map((n) => n.id));
  const allNodes = [...NODES, ...extraNodes];

  return (
    <Box w="100%" h="100%" px="md">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <Stack gap="xs" style={{ flex: 1, minHeight: 0 }}>
          <SlideTitle title={title} subtitle={subtitle} />

          <Box style={{ flex: 1, minHeight: 0 }}>
            <svg
              viewBox={`0 0 ${EXTENDED_CW} ${CH}`}
              className={classes.diagram}
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                <marker
                  id="sf-arrowhead"
                  viewBox="0 0 10 10"
                  refX="9"
                  refY="5"
                  markerWidth="5"
                  markerHeight="5"
                  orient="auto-start-reverse"
                >
                  <path
                    d="M1.5 1.5 L8.5 5 L1.5 8.5"
                    fill="none"
                    stroke="context-stroke"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </marker>
              </defs>

              {/* Domains */}
              {DOMAINS.map((d) => {
                const c = DOMAIN_COLORS[d.cls];
                if (!c) return null;
                return (
                  <g key={d.id} opacity={0.4}>
                    <rect
                      x={d.x}
                      y={d.y}
                      width={d.w}
                      height={d.h}
                      rx={14}
                      fill={c.fill}
                      stroke={c.stroke}
                      strokeWidth={1.5}
                    />
                    <text
                      x={d.x + 14}
                      y={d.y + 24}
                      fill={c.label}
                      fontSize={11}
                      fontWeight={700}
                      letterSpacing="0.06em"
                    >
                      {d.label.toUpperCase()}
                    </text>
                  </g>
                );
              })}

              {/* Subgroups */}
              {SUBGROUPS.map((sg) => {
                const dom = domainMap.get(sg.domainId);
                if (!dom) return null;
                return (
                  <g key={sg.id} opacity={0.25}>
                    <rect
                      x={dom.x + sg.x}
                      y={dom.y + sg.y}
                      width={sg.w}
                      height={sg.h}
                      rx={10}
                      fill="none"
                      stroke="rgba(255,255,255,0.12)"
                      strokeWidth={1}
                      strokeDasharray="4 3"
                    />
                    <text
                      x={dom.x + sg.x + 12}
                      y={dom.y + sg.y - 2}
                      fill="rgba(255,255,255,0.4)"
                      fontSize={9}
                      fontWeight={600}
                      letterSpacing="0.06em"
                    >
                      {sg.label.toUpperCase()}
                    </text>
                  </g>
                );
              })}

              {/* Base arrows (heavily dimmed) */}
              {ARROWS.map((a, i) => {
                const path = computePath(a.from, a.to, extraNodeMap);
                if (!path) return null;
                return (
                  <path
                    key={`base-${a.from}-${a.to}-${i}`}
                    d={path}
                    fill="none"
                    stroke="rgba(255,255,255,0.15)"
                    strokeWidth={1}
                    strokeLinecap="round"
                    opacity={0.15}
                  />
                );
              })}

              {/* Nodes */}
              {allNodes.map((n) => {
                const pos = resolveAbsPos(n);
                const isExtra = extraNodeIds.has(n.id);
                const isHighlighted = currentStep.nodes.has(n.id);
                const c = NODE_COLORS[n.color];
                const Icon = n.icon;

                const isVisibleExtra = isExtra && isHighlighted;
                const nodeOpacity = isExtra
                  ? isVisibleExtra
                    ? 1
                    : 0
                  : isHighlighted
                    ? 1
                    : 0.12;

                return (
                  <g
                    key={n.id}
                    style={{
                      opacity: nodeOpacity,
                      transition: "opacity 0.5s ease",
                    }}
                  >
                    <rect
                      x={pos.x}
                      y={pos.y}
                      width={NW}
                      height={NH}
                      rx={9}
                      fill={c.fill}
                      stroke={c.stroke}
                      strokeWidth={1}
                    />
                    {isHighlighted && (
                      <rect
                        x={pos.x - 2}
                        y={pos.y - 2}
                        width={NW + 4}
                        height={NH + 4}
                        rx={11}
                        fill="none"
                        stroke={c.stroke}
                        strokeWidth={1}
                        className={classes.nodeGlow}
                      />
                    )}
                    <foreignObject
                      x={pos.x}
                      y={pos.y}
                      width={NW}
                      height={NH}
                    >
                      <div className={classes.svgNode}>
                        <div
                          className={classes.svgNodeIcon}
                          style={{ background: c.iconBg }}
                        >
                          <Icon size={14} stroke={2} />
                        </div>
                        <span
                          className={classes.svgNodeLabel}
                          style={{ color: c.label }}
                        >
                          {n.label}
                        </span>
                      </div>
                    </foreignObject>
                  </g>
                );
              })}

              {/* Highlighted arrows per step */}
              {steps.map((stepDef, si) => (
                <g
                  key={stepDef.key}
                  style={{
                    opacity: step === si ? 1 : 0,
                    transition: "opacity 0.5s ease",
                  }}
                >
                  {stepDef.arrows.map((a, ai) => {
                    if (a.bidirectional) {
                      const pathFwd = computePath(
                        a.from,
                        a.to,
                        extraNodeMap,
                      );
                      const pathRev = computePath(
                        a.to,
                        a.from,
                        extraNodeMap,
                      );
                      return (
                        <g key={`${a.from}-${a.to}-${ai}`}>
                          {pathFwd && (
                            <path
                              d={pathFwd}
                              fill="none"
                              stroke={a.color}
                              strokeWidth={2.5}
                              strokeLinecap="round"
                              markerEnd="url(#sf-arrowhead)"
                              className={classes.flowPath}
                              style={{
                                filter: `drop-shadow(0 0 4px ${a.color})`,
                              }}
                            />
                          )}
                          {pathRev && (
                            <path
                              d={pathRev}
                              fill="none"
                              stroke={a.color}
                              strokeWidth={2.5}
                              strokeLinecap="round"
                              markerEnd="url(#sf-arrowhead)"
                              className={classes.flowPath}
                              style={{
                                filter: `drop-shadow(0 0 4px ${a.color})`,
                              }}
                            />
                          )}
                        </g>
                      );
                    }

                    const path = computePath(a.from, a.to, extraNodeMap);
                    if (!path) return null;
                    return (
                      <path
                        key={`${a.from}-${a.to}-${ai}`}
                        d={path}
                        fill="none"
                        stroke={a.color}
                        strokeWidth={2.5}
                        strokeLinecap="round"
                        markerEnd="url(#sf-arrowhead)"
                        className={classes.flowPath}
                        style={{
                          filter: `drop-shadow(0 0 4px ${a.color})`,
                        }}
                      />
                    );
                  })}
                </g>
              ))}
            </svg>
          </Box>

          {/* Caption */}
          <Box className={classes.caption}>
            <div className={classes.stepDots}>
              {steps.map((s, i) => (
                <div
                  key={s.key}
                  className={classes.dot}
                  style={{
                    background:
                      i === step
                        ? "var(--customer-accent-light, var(--mantine-color-emerald-4))"
                        : "rgba(255,255,255,0.15)",
                  }}
                />
              ))}
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Text
                  ta="center"
                  c="dimmed"
                  size="sm"
                  fw={600}
                  tt="uppercase"
                  style={{ letterSpacing: "0.06em" }}
                >
                  {currentStep.label}
                </Text>
                <Text
                  ta="center"
                  c="gray.3"
                  size="lg"
                  lh={1.5}
                  maw={800}
                  mx="auto"
                >
                  {currentStep.caption}
                </Text>
              </motion.div>
            </AnimatePresence>
          </Box>
        </Stack>
      </motion.div>
    </Box>
  );
}
